export function parseDate(dateString) {
  if (dateString instanceof Date) {
    return dateString;
  }
  const parts = dateString.split('/');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  return new Date(year, month, day);
}

export function adjustValueIfZeroInSecondDecimalSpot(value) {
  const valueTwoDecimalSpots = value.toFixed(2);
  let retVal = valueTwoDecimalSpots;
  if (valueTwoDecimalSpots.charAt(valueTwoDecimalSpots.length - 1) === '0') {
    retVal = parseFloat(valueTwoDecimalSpots) + 0.01;
  }
  return parseFloat(retVal);
}

/**
 * Linear debt interpolation for the live clock.
 *
 * Rate is derived from startDebt→endDebt over startDate→endDate. Elapsed time is
 * measured from startDate and is intentionally NOT clamped to endDate: after the
 * configured end date the displayed total keeps rising (or falling) at the same
 * rate until editors publish updated figures.
 */
export function calculateMainStartValueAndIncrementRate(
  clockData,
  intervalInMilliSeconds,
  isShare = false,
  now = new Date()
) {
  const { startDebt, endDebt, startDate, endDate, population } = clockData;
  const startDateParsed = parseDate(startDate);
  const endDateParsed = parseDate(endDate);
  const currentDay = now instanceof Date ? now : parseDate(now);
  // Do not clamp to endDate — extrapolate past the published window at the same rate.
  const secondsPassedSinceStartDate = Math.max(0, (currentDay - startDateParsed) / 1000);
  const secondsBetweenEndStartDate = Math.abs(endDateParsed - startDateParsed) / 1000;

  let startValue;
  let incrementRatePerSecond;

  if (isShare) {
    const debtShareStart = startDebt / population;
    const debtShareFinal = endDebt / population;
    const debtClockFederalIncrementRate =
      (endDebt - startDebt) / secondsBetweenEndStartDate;
    const debtClockFederalStartValue =
      startDebt + debtClockFederalIncrementRate * secondsPassedSinceStartDate;

    startValue = adjustValueIfZeroInSecondDecimalSpot(
      debtClockFederalStartValue / population
    );
    incrementRatePerSecond =
      (debtShareFinal - debtShareStart) / secondsBetweenEndStartDate;
  } else {
    incrementRatePerSecond = (endDebt - startDebt) / secondsBetweenEndStartDate;
    startValue = adjustValueIfZeroInSecondDecimalSpot(
      startDebt + incrementRatePerSecond * secondsPassedSinceStartDate
    );
  }

  const incrementRatePerInterval =
    incrementRatePerSecond * (intervalInMilliSeconds / 1000);

  // Per-person share ticks can be tiny; rounding to 2 decimals zeroes them out.
  const incrementPrecision = isShare ? 6 : 2;

  return {
    startValue: parseFloat(startValue),
    incrementRate: parseFloat(incrementRatePerInterval.toFixed(incrementPrecision)),
  };
}

export function resolveProvince(province, debtData) {
  const requested = province || 'Federal';
  if (!debtData) {
    return requested;
  }
  if (debtData[requested]) {
    return requested;
  }
  // Do not fall back to another province — that shows the wrong clock title/numbers.
  return requested;
}
